import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  Platform,
  TouchableOpacity,
  FlatList,
  Modal,
} from "react-native";
import { useTranslation } from "react-i18next";
import type { TextInput as RNTextInput } from "react-native";
import { mapiProfileSearch } from "../../../../../services/mapi.service";
import { SessionState, useSessionStore } from "../../../../../../../state/src/useSessionStore";
import FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import { useApiWithLoading } from "../../../../../hooks/useApiWithLoading";
import { useGlobalError } from "../../../../uiComponents/GlobalErrorProvider";
import CloseIcon from "../../../../../../../../assets/images/CloseIcon.svg";
import { capturePosthogEvent } from "../../../../../utils/analytics";

type Props = {
  onValidationChange?: (valid: boolean) => void;
  onProfilesFound?: (profiles: any[]) => void;
  onNoProfilesFound?: () => void;
  testID?: string | undefined;
};

export type MapiEnterPhoneStepHandle = {
  submit: () => Promise<any>;
};

const MapiEnterPhoneStep = forwardRef<MapiEnterPhoneStepHandle, Props>(
  ({ onValidationChange, onProfilesFound, onNoProfilesFound, testID }: Props, ref) => {
    const { t } = useTranslation();
    const { executeWithLoading } = useApiWithLoading();
    const { showError } = useGlobalError();
    const [phoneError, setPhoneError] = useState("");

    const agentId = useSessionStore((s: SessionState) => s.agentId);
    const setPhoneNumberStore = useSessionStore(
      (s: SessionState) => (s as any).setPhoneNumber
    );
    const storedPhoneNumber =
      useSessionStore((s: SessionState) => (s as any).phoneNumber) || "";
    const [phoneNumber, setPhoneNumber] = useState(storedPhoneNumber);
    const inputRef = useRef<RNTextInput | null>(null);

    const [profileResults, setProfileResults] = useState<any>(null);
    const [isProfileModalVisible, setIsProfileModalVisible] = useState(false);

    useEffect(() => {
      const id = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(id);
    }, []);

    useEffect(() => {
      setPhoneNumber(storedPhoneNumber || "");
    }, [storedPhoneNumber]);

    const formatPhoneNumber = (number: string) => {
      const cleaned = number.replace(/\D/g, "");
      if (cleaned.length <= 3) return cleaned;
      if (cleaned.length <= 6)
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    };

    const handlePhoneChange = (text: string) => {
      if (phoneError) setPhoneError("");
      const cleaned = text.replace(/\D/g, "");
      if (cleaned.length <= 10) {
        setPhoneNumber(cleaned);
        try {
          setPhoneNumberStore?.(cleaned.length === 10 ? cleaned : null);
        } catch {}
        onValidationChange?.(cleaned.length === 10);
      }
    };

    const submit = async () => {
      if (phoneNumber.length !== 10) {
        setPhoneError("Please enter a valid 10-digit phone number");
        throw new Error("Invalid phone number");
      }
      if (!agentId) {
        showError(
          "Missing agent information. Please go back and try again."
        );
        throw new Error("Missing agent");
      }

      let result: any = null;
      await executeWithLoading(async () => {
        try {
          const response = await mapiProfileSearch({
            agentPartnerId: agentId,
            mobilePhone: phoneNumber,
          });
          setProfileResults(response);

          const profiles = response?.data?.profiles || [];
          if (profiles.length > 0) {
            setIsProfileModalVisible(true);
            result = response;
          } else {
            onNoProfilesFound?.();
            result = { noProfiles: true };
          }
        } catch (error: any) {
          showError(error?.message || "Profile search failed.");
          throw error;
        }
      }, "Searching profiles...");

      capturePosthogEvent("Mapi Send Flow - Phone Capture Step", {
        "data-mg-phone-number": phoneNumber,
      });

      return result;
    };

    useImperativeHandle(ref, () => ({ submit }));

    const handleProfileSelected = (profile: any) => {
      setIsProfileModalVisible(false);
      onProfilesFound?.([profile]);
    };

    const renderProfileModal = () => {
      const profiles = profileResults?.data?.profiles || [];

      return (
        <View style={styles.modalOverlay}>
          <View style={styles.bottomSheet}>
            <TouchableOpacity
              style={styles.modalCloseRow}
              onPress={() => setIsProfileModalVisible(false)}
              testID="mapiProfileModalClose"
            >
              <CloseIcon width={24} height={24} />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>
              Profiles Found ({profiles.length})
            </Text>
            <Text style={styles.modalSubtitle}>
              Select a profile to continue
            </Text>

            <FlatList
              data={profiles}
              keyExtractor={(_, i) => String(i)}
              style={{ maxHeight: 350 }}
              showsVerticalScrollIndicator={false}
              renderItem={({ item }) => {
                const name = item?.name || {};
                const fullName = [
                  name.firstName,
                  name.middleName,
                  name.lastName,
                ]
                  .filter(Boolean)
                  .join(" ");
                const addr = item?.address || {};
                const phone = item?.mobilePhone?.number || "";
                const dob = item?.dateofBirth || "";
                const profileId = item?.profileId || "";

                return (
                  <TouchableOpacity
                    style={styles.profileCard}
                    onPress={() => handleProfileSelected(item)}
                    testID={`mapiProfile-${profileId}`}
                  >
                    <View style={styles.profileCardHeader}>
                      <View style={styles.profileAvatar}>
                        <Text style={styles.profileAvatarText}>
                          {(name.firstName?.[0] || "").toUpperCase()}
                          {(name.lastName?.[0] || "").toUpperCase()}
                        </Text>
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.profileName}>{fullName}</Text>
                        <Text style={styles.profileDetail}>
                          ID: {profileId}
                        </Text>
                      </View>
                    </View>
                    {!!phone && (
                      <Text style={styles.profileDetail}>
                        Phone: +
                        {item?.mobilePhone?.countryDialCode || "1"} {phone}
                      </Text>
                    )}
                    {!!dob && (
                      <Text style={styles.profileDetail}>DOB: {dob}</Text>
                    )}
                    {!!addr.city && (
                      <Text style={styles.profileDetail}>
                        {[
                          addr.line1,
                          addr.city,
                          addr.countrySubdivisionCode,
                          addr.postalCode,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </Text>
                    )}
                  </TouchableOpacity>
                );
              }}
            />
          </View>
        </View>
      );
    };

    return (
      <View style={styles.container} testID={testID}>
        <Text style={styles.title}>{t("PHONE_STEP_TITLE")}</Text>
        <Text style={styles.subtitle}>
          Enter the customer's phone number to search MAPI profiles.
        </Text>
        <FloatingTextInputComponent
          testID="mapi-phone-input"
          label={t("PHONE_STEP_PLACEHOLDER")}
          value={formatPhoneNumber(phoneNumber)}
          onChangeText={handlePhoneChange}
          keyboardType="number-pad"
          errorText={phoneError}
          isAutoFocus={true}
        />

        <Modal
          visible={isProfileModalVisible}
          transparent
          animationType="slide"
          onRequestClose={() => setIsProfileModalVisible(false)}
        >
          {renderProfileModal()}
        </Modal>
      </View>
    );
  }
);

export default MapiEnterPhoneStep;

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8,
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: "400",
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  bottomSheet: {
    backgroundColor: Colors.white,
    padding: 24,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: "85%",
  },
  modalCloseRow: { alignSelf: "flex-end", marginBottom: 16 },
  modalTitle: {
    fontFamily: "Rubik",
    fontSize: 24,
    fontWeight: "500",
    color: Colors.accountLock,
    lineHeight: 28,
    marginBottom: 8,
  },
  modalSubtitle: {
    fontFamily: "Rubik",
    fontSize: 14,
    fontWeight: "400",
    color: Colors.subTitleColor,
    lineHeight: 20,
    marginBottom: 20,
  },
  profileCard: {
    backgroundColor: Colors.textInputBackground,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  profileCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  profileAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: Colors.titleTextColor,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  profileAvatarText: {
    fontFamily: "Rubik",
    fontSize: 14,
    fontWeight: "600",
    color: Colors.white,
  },
  profileName: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 22,
  },
  profileDetail: {
    fontFamily: "Rubik",
    fontSize: 13,
    fontWeight: "400",
    color: Colors.infoSubValue,
    lineHeight: 18,
    marginTop: 2,
  },
});
